<template>
  <div>
    <div class="top-actions">
      <button
        type="button"
        class="icon-btn theme-button"
        :aria-label="'Thème : ' + themeLabels[themePref] + ' (cliquer pour changer)'"
        :title="themeLabels[themePref]"
        @click="cycleTheme"
      >
        <transition name="icon-swap" mode="out-in">
          <ThemeIcon :key="themePref" :mode="themePref" />
        </transition>
      </button>
      <a
        class="icon-btn ask-button"
        :href="notebookUrl"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Poser une question (ouvre un nouvel onglet)"
        title="Poser une question"
        @click.prevent="openNotebook"
      >
        ?
      </a>
    </div>
    <div class="header">
      <div class="header-img">
        <img class="logo logo-light" src="./static/img/Logo.png" alt="Abby Tools" />
        <img class="logo logo-dark" src="./static/img/LogoDark.png" alt="" aria-hidden="true" />
      </div>
    </div>
    <Meals />
    <WalkTracker />
    <LessonsViewer />
  </div>
</template>

<script>
import Meals from "./components/Meals.vue";
import LessonsViewer from "./components/LessonsViewer.vue";
import WalkTracker from "./components/WalkTracker.vue";

// icône du thème : soleil (clair), téléphone (auto) ou lune (sombre)
const ThemeIcon = {
  functional: true,
  props: { mode: String },
  render(h, { props }) {
    const svg = (children) =>
      h("svg", { class: "icon", attrs: { viewBox: "0 0 24 24", "aria-hidden": "true" } }, children);
    if (props.mode === "light") {
      return svg([
        h("circle", { attrs: { cx: 12, cy: 12, r: 4.2 } }),
        h("path", {
          attrs: {
            d: "M12 2.5v2.2M12 19.3v2.2M4.6 4.6l1.6 1.6M17.8 17.8l1.6 1.6M2.5 12h2.2M19.3 12h2.2M4.6 19.4l1.6-1.6M17.8 6.2l1.6-1.6",
          },
        }),
      ]);
    }
    if (props.mode === "auto") {
      // moitié soleil (rayons à gauche), moitié nuit (demi-disque plein à droite)
      return svg([
        h("path", { class: "icon-fill", attrs: { d: "M12 6.5a5.5 5.5 0 0 1 0 11z" } }),
        h("circle", { attrs: { cx: 12, cy: 12, r: 5.5 } }),
        h("path", {
          attrs: {
            d: "M12 1.8v2M12 20.2v2M4.6 4.6l1.4 1.4M4.6 19.4l1.4-1.4M1.8 12h2",
          },
        }),
      ]);
    }
    return svg([
      h("path", { class: "icon-fill", attrs: { d: "M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z" } }),
    ]);
  },
};

export default {
  components: { LessonsViewer, WalkTracker, Meals, ThemeIcon },
  data() {
    return {
      notebookUrl:
        process.env.VUE_APP_NOTEBOOKLM_URL ||
        "https://notebooklm.google.com/notebook/9bb9742b-e51b-425e-83ab-61a9b3a082b0",
      theme: document.documentElement.getAttribute("data-theme") || "light",
      // préférence choisie : "light", "auto" (suit le téléphone) ou "dark"
      themePref: document.documentElement.getAttribute("data-theme-pref") || "auto",
      // ordre de passage d'un mode à l'autre au clic
      themeModes: ["light", "dark", "auto"],
      themeLabels: {
        light: "Mode clair",
        auto: "Suivre le téléphone",
        dark: "Mode sombre",
      },
    };
  },
  mounted() {
    // en mode auto, suit le mode clair/sombre du téléphone en direct
    this._systemDark = window.matchMedia("(prefers-color-scheme: dark)");
    this._onSystemChange = () => {
      if (this.themePref === "auto") this.applyTheme(this.systemTheme());
    };
    if (this._systemDark.addEventListener) {
      this._systemDark.addEventListener("change", this._onSystemChange);
    } else {
      this._systemDark.addListener(this._onSystemChange); // anciens Safari
    }
    // Safari met la page en pause en arrière-plan : on revérifie au retour
    this._onVisible = () => {
      if (!document.hidden) this._onSystemChange();
    };
    document.addEventListener("visibilitychange", this._onVisible);
  },
  beforeDestroy() {
    document.removeEventListener("visibilitychange", this._onVisible);
    if (this._systemDark.removeEventListener) {
      this._systemDark.removeEventListener("change", this._onSystemChange);
    } else {
      this._systemDark.removeListener(this._onSystemChange);
    }
  },
  methods: {
    systemTheme() {
      return this._systemDark.matches ? "dark" : "light";
    },
    cycleTheme() {
      const i = this.themeModes.indexOf(this.themePref);
      this.setThemePref(this.themeModes[(i + 1) % this.themeModes.length]);
    },
    setThemePref(pref) {
      this.themePref = pref;
      document.documentElement.setAttribute("data-theme-pref", pref);
      this.applyTheme(pref === "auto" ? this.systemTheme() : pref);
      this.saveThemePref(pref);
    },
    applyTheme(theme) {
      if (theme === this.theme) return;
      const root = document.documentElement;
      this.theme = theme;
      // transition douce des couleurs uniquement pendant le changement de thème
      root.classList.add("theme-anim");
      root.setAttribute("data-theme", theme);
      clearTimeout(this._themeAnimTimer);
      this._themeAnimTimer = setTimeout(() => root.classList.remove("theme-anim"), 450);
    },
    saveThemePref(pref) {
      try {
        localStorage.setItem("abby-theme", pref);
      } catch (e) {
        // stockage indisponible : le cookie prend le relais
      }
      document.cookie = `abby-theme=${pref}; max-age=31536000; path=/; SameSite=Lax`;
    },
    openNotebook() {
      // assure l’ouverture dans un nouvel onglet même si l’attribut target est modifié
      window.open(this.notebookUrl, "_blank", "noopener,noreferrer");
    },
  },
};
</script>

<style>
:root {
  color-scheme: light;
  --page-text: #111827;
  --surface: #fff;
  --surface-2: #f8fafc;
  --surface-hover: #f1f5f9;
  --border: #e5e7eb;
  --border-2: #dbe3ea;
  --text: #0f172a;
  --text-2: #475569;
  --text-muted: #6b7280;
  --text-faint: #9ca3af;
  --btn-primary-bg: #111827;
  --chip-bg: #eef2ff;
  --mark-bg: #d3d3d3;
  --top-btn-bg: rgba(255, 255, 255, 0.85);
  --top-btn-fg: #111827;
  --top-btn-border: rgba(15, 23, 42, 0.08);
}

:root[data-theme="dark"] {
  color-scheme: dark;
  --page-text: #e6eaf0;
  --surface: #121b38;
  --surface-2: #17213f;
  --surface-hover: #202c52;
  --border: #26345e;
  --border-2: #2c3b68;
  --text: #e6eaf0;
  --text-2: #b6c0dc;
  --text-muted: #8c98bc;
  --text-faint: #6a769c;
  --btn-primary-bg: #2b3b72;
  --chip-bg: #1c2850;
  --mark-bg: #33427a;
  --top-btn-bg: rgba(18, 27, 56, 0.85);
  --top-btn-fg: #f3f4f6;
  --top-btn-border: rgba(255, 255, 255, 0.1);

  /* variables de LessonsViewer */
  --line: #26345e;
  --ok-bg: #0e2a2c;
  --ok-border: #10b981;
  --ok-text: #6ee7b7;
}

/* Fondu des couleurs pendant le changement de thème */
.theme-anim,
.theme-anim *:not(.icon),
.theme-anim *::before,
.theme-anim *::after {
  transition: background-color 0.4s ease, color 0.4s ease,
    border-color 0.4s ease, box-shadow 0.4s ease, opacity 0.4s ease !important;
}

/* couleur unie visible quand on tire la page au-delà des bords (iPhone) */
html {
  background: #efebe3;
}
html[data-theme="dark"] {
  background: #0a0d12;
}

body {
  font-family: sans-serif;
  margin: 0;
  padding: 20px;
  color: var(--page-text);
  /* background: #f7f7f7; */
}

/* Motifs de fond : clair (::before) et sombre sous un voile noir
   (::after) qui apparaît en fondu par-dessus ; ils couvrent toute la page
   et défilent avec elle */
body {
  position: relative;
  min-height: 100vh;
  box-sizing: border-box;
}
body::before,
body::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  background-repeat: repeat;
}
body::before {
  background-color: #e8e2d66b;
  background-image: url("./static/img/Background.png");
  background-size: 800px;
  background-blend-mode: lighten;
}
body::after {
  background-color: #141a24;
  background-image: linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)),
    url("./static/img/BackgroundDark.png");
  background-repeat: repeat;
  background-size: auto, 800px;
  opacity: 0;
  transition: opacity 0.4s ease;
}
html[data-theme="dark"] body::after {
  opacity: 1;
}

/* Ajustements des fonds teintés en mode sombre */
html[data-theme="dark"] .need-btn.photo {
  background: #14261c;
  border-color: #1f4630;
}
html[data-theme="dark"] .edit-counter.pee {
  background: #15233a;
  border-color: #23406b;
}
html[data-theme="dark"] .edit-counter.poop {
  background: #2a2214;
  border-color: #5c4718;
}
html[data-theme="dark"] .edit-stepper button {
  border-color: rgba(255, 255, 255, 0.12);
}
html[data-theme="dark"] .edit-field input {
  background: var(--surface-2);
  border-color: var(--border);
}
html[data-theme="dark"] .mark[disabled] {
  background: #14342a;
}
html[data-theme="dark"] .page-btn.active {
  border-color: var(--btn-primary-bg);
}

/* pas de zoom au double-tap sur les éléments cliquables (mobile) ;
   le zoom à deux doigts reste possible */
button,
a,
[role="button"] {
  touch-action: manipulation;
}

/* Boutons fixes en haut à droite */
.top-actions {
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 1000;
  display: flex;
  gap: 8px;
}

.icon-btn {
  position: relative;
  width: 40px;
  height: 40px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid var(--top-btn-border);
  background: var(--top-btn-bg);
  color: var(--top-btn-fg);
  -webkit-backdrop-filter: blur(8px);
  backdrop-filter: blur(8px);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
  cursor: pointer;
  text-decoration: none;
  font-family: inherit;
  font-size: 19px;
  font-weight: 700;
  line-height: 1;
  overflow: hidden;
  transition: transform 0.15s ease, box-shadow 0.2s ease,
    background-color 0.4s ease, color 0.4s ease, border-color 0.4s ease;
  -webkit-tap-highlight-color: transparent;
}

.icon-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
}

.icon-btn:active {
  transform: scale(0.94);
}

.icon-btn:focus-visible {
  outline: 2px solid #f5b72a;
  outline-offset: 2px;
}

/* Bouton du thème : passe d'un mode à l'autre au clic */
.icon-btn .icon {
  width: 20px;
  height: 20px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  flex: 0 0 auto;
}
.icon-btn .icon-fill {
  fill: currentColor;
  stroke: none;
}
/* l'icône du bouton pivote en changeant */
.icon-swap-enter-active,
.icon-swap-leave-active {
  transition: transform 0.2s ease, opacity 0.2s ease;
}
.icon-swap-enter {
  transform: rotate(-90deg) scale(0.5);
  opacity: 0;
}
.icon-swap-leave-to {
  transform: rotate(90deg) scale(0.5);
  opacity: 0;
}

@media (max-width: 420px) {
  .top-actions {
    top: 10px;
    right: 10px;
    gap: 6px;
  }
  .icon-btn {
    width: 36px;
    height: 36px;
    font-size: 17px;
  }
  .icon-btn .icon {
    width: 18px;
    height: 18px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .icon-swap-enter-active,
  .icon-swap-leave-active,
  body::after {
    transition: none;
  }
}

.header {
  width: 100%;
  display: flex;
  justify-content: center;
}
.header-img {
  position: relative;
  width: 200px;
  transition: transform 0.1s ease-in-out;
}
.logo {
  display: block;
  width: 100%;
  transition: opacity 0.4s ease;
}
/* les deux logos sont superposés et se croisent en fondu ;
   le logo sombre prend la hauteur du dessin du logo clair et se centre dessus.
   LogoDark.png a ~6% de marge transparente en haut/bas (dessin = 88.3% de la
   hauteur) : 98.8% / 0.883 ≈ 112% pour que les dessins aient la même hauteur */
.logo-dark {
  position: absolute;
  top: 50.6%;
  left: 49.5%;
  width: auto;
  height: 112%;
  transform: translate(-50%, -50%);
  opacity: 0;
}
html[data-theme="dark"] .logo-light {
  opacity: 0;
}
html[data-theme="dark"] .logo-dark {
  opacity: 1;
}
.header-img:hover {
  transform: scale(1.1) rotate(-4deg);
}
</style>
